import { useCallback, useEffect, useRef } from 'react'
import useProjectId from '../../customHooks/useProjectId'
import { useAccessToken } from '../useAccessToken'
import { useHandleError } from '../useHandleError'
import { FetchResponse, makeResultFetch } from '../genericFetch'
import { apiRequestParams } from '../fetchOptions'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { AccountComment, PeriodReportData } from '../queries/PeriodReportData'

export interface SavePeriodReportCommentsPayload {
  reportComment: string
  accountComments: AccountComment[]
}

export const useSavePeriodReportComments = (reportGuid: string | null) => {
  const getAccessToken = useAccessToken()
  const handleError = useHandleError()
  const projectId = useProjectId()
  const queryClient = useQueryClient()
  const abortControllerRef = useRef<AbortController | null>(null)

  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort()
    }
  }, [])

  const mutationFunction = useCallback(
    async (payload: SavePeriodReportCommentsPayload) => {
      if (!reportGuid) {
        throw new Error('Report GUID is required')
      }

      abortControllerRef.current?.abort()
      const controller = new AbortController()
      abortControllerRef.current = controller

      return getAccessToken().then((accessToken) => {
        if (!accessToken) throw new Error('Access token is undefined')

        const trimmedPayload: SavePeriodReportCommentsPayload = {
          reportComment: payload.reportComment?.trim() || '',
          accountComments: payload.accountComments
            ? payload.accountComments.map((c) => ({
                ...c,
                text: c.text?.trim() || '',
              }))
            : [],
        }

        const fetchedData = makeResultFetch<PeriodReportData>(
          `/reports/${projectId}/projectForecast/${reportGuid}/comments`,
          apiRequestParams(
            accessToken,
            'PUT',
            trimmedPayload,
            controller.signal
          )
        ).then((response: FetchResponse<PeriodReportData>) => {
          const { message, success } = response

          if (!success) throw Error(message)

          return response
        })

        return fetchedData
      })
    },
    [getAccessToken, projectId, reportGuid]
  )

  const handleSuccess = (_response: FetchResponse<PeriodReportData>) => {
    queryClient.invalidateQueries({
      queryKey: [projectId, 'periodReportSnapshots'],
      refetchType: 'none',
    })
  }

  const handleErrorWithContext = (error: unknown) => {
    // Ignore intentional aborts — a newer request is already in flight
    if (error instanceof DOMException && error.name === 'AbortError') return

    // Mark cache as stale so it refetches on next activation, but don't
    // trigger an immediate background refetch (avoids overwriting local UI state).
    queryClient.invalidateQueries({
      queryKey: [projectId, 'periodReportSnapshots'],
      refetchType: 'none',
    })

    if (error instanceof Error) {
      const contextualError = new Error(
        `Gick inte att spara kommentaren: ${error.message}`
      )
      handleError(contextualError)
    } else {
      handleError(new Error('Gick inte att spara kommentaren'))
    }
  }

  const mutation = useMutation({
    mutationFn: mutationFunction,
    onSuccess: handleSuccess,
    onError: handleErrorWithContext,
  })

  return mutation
}
